/**
 * additional-views/api — katalog widoków dodatkowych (F22, ADR-0087 /
 * PHASE_22_PLAN.md §6). Ustawienia → „Widoki dodatkowe" (mirror `lib/processes/procedures-api.ts`
 * — katalog tenant-globalny, jedno uprawnienie tenant-wide `crm_core.additional_views.manage`
 * bramkuje wszystko: podgląd/CRUD/test).
 *
 * Endpointy (kontrakt z PHASE_22_PLAN.md §6 — trasy budowane równolegle w Ścieżce D/join,
 * ten plik celuje w udokumentowany kontrakt):
 *   GET    /settings/additional-views                 — listing (filtr `parentTableName`/`parentEntity`)
 *   POST   /settings/additional-views                  — utwórz
 *   PATCH  /settings/additional-views/:viewId           — edycja
 *   DELETE /settings/additional-views/:viewId           — soft-delete
 *   POST   /settings/additional-views/test-sql          — podgląd autora (bez zapisu)
 *   GET    /settings/additional-views/:viewId/columns   — kolumny wyniku widoku (bez wierszy)
 *
 * Kształt odpowiedzi listy/detalu (`{ views }` / `{ view }`) mirroruje konwencję siostrzaną
 * `settings/additional-tables` (`{ tables }` / `{ table }`, `AdditionalTablesTab.tsx`) — widoki
 * dodatkowe są jawnie modelowane jako trzeci wariant obok tabel dodatkowych (ADR-0087).
 * `test-sql` zwraca kształt `AdditionalViewData` (zapytanie widoku jest zawsze `SELECT`, nie ma
 * dychotomii walidacja/akcja jak procedury kontrolne procesów) — body niesie przykładowe
 * wartości parametrów wpisane WPROST przez autora (`paramValues`), nie wiązania (autor nie ma
 * jeszcze żadnego konkretnego rekordu podczas projektowania widoku w Ustawieniach).
 */

import type {
  AdditionalViewColumn,
  AdditionalViewColumnsResponse,
  AdditionalViewData,
  AdditionalViewDefinition,
  AdditionalViewParamBindings,
  AdditionalViewSourceParam,
} from '@saas/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/api';

// ============================================================================
// Query keys
// ============================================================================

export const ADDITIONAL_VIEWS_LIST_KEY = ['additional-views'] as const;
export const additionalViewDetailKey = (id: string) => ['additional-views', id] as const;

// ============================================================================
// List
// ============================================================================

export interface ListAdditionalViewsParams {
  parentTableName?: string;
  parentEntity?: string;
}

interface ListResponse {
  views: AdditionalViewDefinition[];
}

interface DetailResponse {
  view: AdditionalViewDefinition;
}

export function useAdditionalViewsList(params: ListAdditionalViewsParams = {}) {
  const { parentTableName, parentEntity } = params;
  return useQuery<AdditionalViewDefinition[]>({
    queryKey: [
      ...ADDITIONAL_VIEWS_LIST_KEY,
      { parentTableName: parentTableName ?? null, parentEntity: parentEntity ?? null },
    ] as const,
    queryFn: async () => {
      const search = new URLSearchParams();
      if (parentTableName) search.set('parentTableName', parentTableName);
      if (parentEntity) search.set('parentEntity', parentEntity);
      const qs = search.toString();
      const res = await api.get<ListResponse>(`/settings/additional-views${qs ? `?${qs}` : ''}`);
      return res.data.views;
    },
  });
}

// ============================================================================
// Kolumny widoku (kreator formatek — układ kolumn widoku SQL)
// ============================================================================

/**
 * Klucz cache kolumn widoku. Leży POD kluczem szczegółu widoku (`['additional-views', id]`), więc
 * zapis widoku w Ustawieniach (`useUpdateAdditionalView` → invalidate po prefiksie) odświeża też
 * kolumny otwartego kreatora — zmiana SQL zmienia kolumny.
 */
export const additionalViewColumnsKey = (id: string) =>
  [...additionalViewDetailKey(id), 'columns'] as const;

/**
 * Kolumny wyniku widoku (`GET /settings/additional-views/:viewId/columns`) — serwer wykonuje
 * zapytanie i zwraca samą definicję kolumn (bez wierszy). Kolumny widoku znane są dopiero po
 * uruchomieniu zapytania, więc to jedyne źródło ich nazw/typów dla układu kolumn.
 *
 * Włączone tylko z `viewId`. Błędy (403 brak uprawnienia, 404 widok nie istnieje, 400/422 powód od
 * bazy, np. „division by zero") są deterministyczne — bez ponowień, żeby powód pojawił się od
 * razu; wołający czyta status i `message` z błędu axios (`use-additional-view-columns-model`).
 */
export function useAdditionalViewColumns(viewId: string | undefined) {
  return useQuery<AdditionalViewColumn[]>({
    queryKey: additionalViewColumnsKey(viewId ?? ''),
    queryFn: async () => {
      if (viewId === undefined) {
        // `enabled` poniżej nie dopuszcza tej ścieżki — jawny błąd zamiast zapytania o pusty id.
        throw new Error('useAdditionalViewColumns: brak id widoku dodatkowego');
      }
      const res = await api.get<AdditionalViewColumnsResponse>(
        `/settings/additional-views/${viewId}/columns`,
      );
      return res.data.columns;
    },
    enabled: viewId !== undefined && viewId !== '',
    retry: false,
    staleTime: 60_000,
  });
}

function useInvalidateAdditionalViews() {
  const queryClient = useQueryClient();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: ADDITIONAL_VIEWS_LIST_KEY });
    if (id) queryClient.invalidateQueries({ queryKey: additionalViewDetailKey(id) });
  };
}

// ============================================================================
// Mutations — create / update / delete
// ============================================================================

export interface CreateAdditionalViewInput {
  displayName: string;
  slug: string;
  parentTableName: string;
  parentEntityType: 'system' | 'custom';
  parentEntity: string;
  description?: string | null;
  sourceSql: string;
  declaredParams: AdditionalViewSourceParam[];
  paramBindings: AdditionalViewParamBindings | null;
}

export function useCreateAdditionalView() {
  const invalidate = useInvalidateAdditionalViews();
  return useMutation({
    mutationFn: async (input: CreateAdditionalViewInput) => {
      // `paramBindings` w Zod (`CreateAdditionalViewSchema`, apps/api/lib/validators/
      // additional-views.ts) jest `dictionaryParamBindingsSchema.optional()` — BEZ
      // `.nullable()`. Wysłanie `null` kończy się 422 VALIDATION_FAILED; pomijamy klucz
      // zamiast tego (brak wiązań = brak klucza, mirror reszty repo).
      const { paramBindings, ...rest } = input;
      const body = paramBindings ? { ...rest, paramBindings } : rest;
      const res = await api.post<DetailResponse>('/settings/additional-views', body);
      return res.data.view;
    },
    onSuccess: () => invalidate(),
  });
}

export interface UpdateAdditionalViewInput {
  displayName?: string;
  description?: string | null;
  sourceSql?: string;
  declaredParams?: AdditionalViewSourceParam[];
  paramBindings?: AdditionalViewParamBindings | null;
  isActive?: boolean;
}

export function useUpdateAdditionalView() {
  const invalidate = useInvalidateAdditionalViews();
  return useMutation({
    mutationFn: async ({ id, values }: { id: string; values: UpdateAdditionalViewInput }) => {
      // `UpdateAdditionalViewSchema.paramBindings` (apps/api/lib/validators/additional-views.ts)
      // jest `.optional()` bez `.nullable()` — PATCH semantyka: klucz NIEOBECNY = "nie dotykaj",
      // więc do wyczyszczenia bindings (user usunął wszystkie parametry) trzeba wysłać `{}`
      // JAWNIE, nie pominąć klucz. Zawsze wysyłamy efektywną wartość (`?? {}`), nigdy `null`.
      const { paramBindings, ...rest } = values;
      const body = { ...rest, paramBindings: paramBindings ?? {} };
      const res = await api.patch<DetailResponse>(`/settings/additional-views/${id}`, body);
      return res.data.view;
    },
    onSuccess: (view) => invalidate(view.id),
  });
}

export function useDeleteAdditionalView() {
  const invalidate = useInvalidateAdditionalViews();
  return useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/settings/additional-views/${id}`);
      return id;
    },
    onSuccess: (id) => invalidate(id),
  });
}

// ============================================================================
// Test-sql (podgląd autora, bez zapisu — ADR-0087 §7)
// ============================================================================

export interface TestAdditionalViewSqlInput {
  sql: string;
  declaredParams: AdditionalViewSourceParam[];
  /** Przykładowe wartości zadeklarowanych parametrów (opcjonalne — autor testuje bez rekordu). */
  paramValues?: Record<string, string | number | boolean | null>;
}

export function useTestAdditionalViewSql() {
  return useMutation({
    mutationFn: async (input: TestAdditionalViewSqlInput): Promise<AdditionalViewData> => {
      const res = await api.post<AdditionalViewData>('/settings/additional-views/test-sql', input);
      return res.data;
    },
  });
}
